import { useEffect, useRef, useState } from "react";
import { Download, Eye, Loader2, Mail } from "lucide-react";
import { chiNotify } from "@/components/ChiNotification";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { trpc } from "@/lib/trpc";
import CrmColumnHeading from "@/components/CrmColumnHeading";

type TicketEmailTestId = "ticket-attendee" | "ticket-purchaser-receipt";

function isTicketEmailTestId(id: string): id is TicketEmailTestId {
  return id === "ticket-attendee" || id === "ticket-purchaser-receipt";
}

export default function EmailNotificationCatalog() {
  const catalog = trpc.googleWorkspace.emailNotificationCatalog.useQuery(undefined, {
    retry: false,
  });
  const ticketTestSettings = trpc.googleWorkspace.ticketEmailTestSettings.useQuery(undefined, {
    retry: false,
  });
  const sampleTicketPdf = trpc.googleWorkspace.ticketEmailTestPdf.useQuery(undefined, {
    enabled: false,
    retry: false,
  });
  type CatalogItem = NonNullable<typeof catalog.data>[number];
  const [selectedId, setSelectedId] = useState<CatalogItem["id"] | null>(null);
  /* Which notification a test is being addressed for, and where it is going. */
  const [testFor, setTestFor] = useState<{ id: CatalogItem["id"]; name: string } | null>(null);
  const [testRecipient, setTestRecipient] = useState("");
  const [ticketTestError, setTicketTestError] = useState<string | null>(null);
  const [samplePdfError, setSamplePdfError] = useState<string | null>(null);
  const [samplePdfPending, setSamplePdfPending] = useState(false);
  const ticketSendLock = useRef(false);
  const sendTest = trpc.googleWorkspace.sendNotificationTest.useMutation({
    onSuccess: result => {
      setTestFor(null);
      chiNotify.success("Test email sent", { description: `Delivered to ${result.recipient}.` });
    },
    onError: error => chiNotify.error("Test email not sent", { description: error.message }),
  });
  const sendTicketEmailTest = trpc.googleWorkspace.sendTicketEmailTest.useMutation({
    retry: false,
    onSuccess: result => {
      ticketSendLock.current = false;
      setTestFor(null);
      setTicketTestError(null);
      chiNotify.success("Ticket email test sent", { description: `Delivered to ${result.recipient}.` });
    },
    onError: error => {
      ticketSendLock.current = false;
      setTicketTestError(`${error.message} Check your inbox before explicitly retrying; delivery may be delayed or may have succeeded despite this error.`);
    },
  });

  useEffect(() => {
    if (!selectedId && catalog.data?.[0]) setSelectedId(catalog.data[0].id);
  }, [catalog.data, selectedId]);

  const preview = trpc.googleWorkspace.emailNotificationPreview.useQuery(
    selectedId ?? "secure-sign-in-code",
    { enabled: Boolean(selectedId), retry: false },
  );
  const selected = catalog.data?.find(item => item.id === selectedId);
  const isTicketTest = Boolean(testFor && isTicketEmailTestId(testFor.id));
  const ticketSettings = ticketTestSettings.data;
  const ticketTestBlocked = import.meta.env.PROD
    || ticketTestSettings.isLoading
    || Boolean(ticketTestSettings.error)
    || ticketSettings?.testDeliveryEnabled !== true
    || !ticketSettings?.recipient;
  const ticketTestBlockedReason = ticketTestSettings.isLoading
    ? "Checking whether test delivery is enabled…"
    : ticketTestSettings.error
      ? "Test delivery settings could not be verified. Sending stays disabled until they can be checked."
      : import.meta.env.PROD
        ? "Test sends are disabled in production. Preview and sample PDF downloads remain available."
        : ticketSettings?.testDeliveryEnabled !== true
          ? ticketSettings?.disabledReason || "Test delivery is disabled by the workspace."
          : !ticketSettings.recipient
            ? "No signed-in owner email is available as a test recipient."
            : "";

  async function downloadTicketSamplePdf() {
    if (samplePdfPending) return;
    setSamplePdfPending(true);
    setSamplePdfError(null);
    try {
      const result = await sampleTicketPdf.refetch();
      if (result.error) throw result.error;
      if (!result.data) throw new Error("The sample PDF was not returned.");
      const binary = atob(result.data.base64);
      const bytes = Uint8Array.from(binary, character => character.charCodeAt(0));
      const blob = new Blob([bytes], { type: result.data.contentType || "application/pdf" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = result.data.filename;
      anchor.style.display = "none";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1_500);
    } catch (error) {
      setSamplePdfError(error instanceof Error ? error.message : "The sample PDF could not be downloaded.");
    } finally {
      setSamplePdfPending(false);
    }
  }

  return (
    /* A fragment: the catalogue and its test dialog are siblings. */
    <>
    <section className="email-catalog" aria-labelledby="email-catalog-title">
      <header className="email-catalog__head">
        <div>
          <p className="email-catalog__eyebrow">
            <Mail size={13} aria-hidden="true" /> Email notifications
          </p>
          <h2 id="email-catalog-title">What the system sends</h2>
          <p>
            Choose a notification to inspect the exact HTML email layout. Every preview uses
            fictional business data and does not send a message.
          </p>
        </div>
        {catalog.data && (
          <span className="email-catalog__count">{catalog.data.length} notifications</span>
        )}
      </header>

      {catalog.isLoading ? (
        <div className="email-catalog__state" role="status">
          <Loader2 className="animate-spin" size={18} aria-hidden="true" /> Loading email catalog…
        </div>
      ) : catalog.error ? (
        <div className="email-catalog__state" role="alert">
          The email catalog could not be loaded. {catalog.error.message}
        </div>
      ) : (
        <div className="email-catalog__workspace">
          <div className="email-catalog__table-wrap">
            <table className="email-catalog__table">
              <thead>
                <tr>
                  <th scope="col"><CrmColumnHeading label="Business use case" /></th>
                  <th scope="col"><CrmColumnHeading label="Name" /></th>
                  <th scope="col"><span className="sr-only">Preview</span></th>
                </tr>
              </thead>
              <tbody>
                {catalog.data?.map(item => {
                  const active = item.id === selectedId;
                  return (
                    <tr key={item.id} data-selected={active || undefined}>
                      <td>{item.businessUseCase}</td>
                      <td>
                        <button
                          type="button"
                          className="email-catalog__select"
                          aria-pressed={active}
                          onClick={() => setSelectedId(item.id)}
                        >
                          <strong>{item.name}</strong>
                          <span>{item.audience}</span>
                        </button>
                      </td>
                      {/*
                        Both actions on one line. The buttons are inline-flex, so in
                        a bare table cell they stacked — a preview icon above an
                        envelope, reading as two rows for one notification.
                      */}
                      <td>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: ".35rem" }}>
                        <button
                          type="button"
                          className="email-catalog__icon-button"
                          aria-label={`Preview ${item.name}`}
                          aria-pressed={active}
                          onClick={() => setSelectedId(item.id)}
                        >
                          <Eye size={15} aria-hidden="true" />
                        </button>
                        {/*
                          Beside the preview rather than inside it: choosing to send
                          is a different decision from choosing to look, and the row
                          is where the notification is identified.
                        */}
                        {isTicketEmailTestId(item.id) ? (
                          <button
                            type="button"
                            className="email-catalog__icon-button"
                            aria-label={`Send test to me: ${item.name}`}
                            title="Send a test to my signed-in owner address"
                            onClick={() => {
                              setTicketTestError(null);
                              setTestFor({ id: item.id, name: item.name });
                            }}
                          >
                            <Mail size={15} aria-hidden="true" />
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="email-catalog__icon-button"
                            aria-label={`Send a test of ${item.name}`}
                            title="Send a test email"
                            onClick={() => { setTestFor({ id: item.id, name: item.name }); setTestRecipient(""); }}
                          >
                            <Mail size={15} aria-hidden="true" />
                          </button>
                        )}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <article className="email-catalog__preview" aria-live="polite">
            <header className="email-catalog__preview-head">
              <div>
                <p>{selected?.businessUseCase ?? "Email preview"}</p>
                <h3>{preview.data?.name ?? selected?.name ?? "Select a notification"}</h3>
              </div>
              <div className="email-catalog__preview-actions">
                <span>HTML preview</span>
                {selected?.id === "ticket-attendee" && (
                  <button
                    type="button"
                    className="email-catalog__download"
                    data-testid="ticket-email-sample-pdf-download"
                    disabled={samplePdfPending}
                    onClick={downloadTicketSamplePdf}
                  >
                    {samplePdfPending ? <Loader2 size={15} className="animate-spin" aria-hidden="true" /> : <Download size={15} aria-hidden="true" />}
                    {samplePdfPending ? "Preparing sample…" : "Download sample PDF"}
                  </button>
                )}
              </div>
            </header>

            {preview.isLoading ? (
              <div className="email-catalog__preview-state" role="status">
                <Loader2 className="animate-spin" size={18} aria-hidden="true" />
                Rendering preview…
              </div>
            ) : preview.error ? (
              <div className="email-catalog__preview-state" role="alert">
                This preview could not be rendered. {preview.error.message}
              </div>
            ) : preview.data ? (
              <>
                <dl className="email-catalog__details">
                  <div>
                    <dt>Subject</dt>
                    <dd>{preview.data.subject}</dd>
                  </div>
                  <div>
                    <dt>Sent to</dt>
                    <dd>{preview.data.audience}</dd>
                  </div>
                  <div>
                    <dt>Trigger</dt>
                    <dd>{preview.data.trigger}</dd>
                  </div>
                </dl>
                <p className="email-catalog__notice">{preview.data.sampleDataNotice}</p>
                {selected?.id === "ticket-attendee" && (
                  <p className="email-catalog__ticket-sample-notice">
                    Fictional sample only. No real orders or attendees are changed. Any QR is watermarked sample artwork—not valid for entry—and no real claim links are included.
                  </p>
                )}
                {samplePdfError && selected?.id === "ticket-attendee" && (
                  <p className="email-catalog__ticket-pdf-error" role="alert">{samplePdfError}</p>
                )}
                <div className="email-catalog__frame-shell">
                  <iframe
                    title={`${preview.data.name} email preview`}
                    sandbox=""
                    srcDoc={preview.data.html}
                  />
                </div>
              </>
            ) : (
              <div className="email-catalog__preview-state">Choose an email from the table.</div>
            )}
          </article>
        </div>
      )}

      <style>{`
        .email-catalog {
          margin-top: 2rem;
          padding-top: 1.75rem;
          border-top: 1px solid rgba(255,255,255,.09);
        }

        .email-catalog__head {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .email-catalog__eyebrow {
          display: inline-flex;
          align-items: center;
          gap: .4rem;
          margin: 0 0 .35rem;
          color: #7DEAFF;
          font-family: 'Space Grotesk', sans-serif;
          font-size: var(--chi-body-compact);
          font-weight: 750;
          letter-spacing: .12em;
          text-transform: uppercase;
        }

        .email-catalog__head h2 {
          margin: 0;
          color: #fff;
          font-family: 'Bebas Neue', sans-serif;
          font-size: clamp(1.7rem, 3vw, 2.35rem);
          letter-spacing: .025em;
          line-height: 1;
        }

        .email-catalog__head > div > p:last-child {
          max-width: 65ch;
          margin: .55rem 0 0;
          color: #8E9AAF;
          font-family: 'Inter', sans-serif;
          font-size: var(--chi-body-compact);
          line-height: 1.55;
        }

        .email-catalog__count {
          flex-shrink: 0;
          padding: .38rem .7rem;
          border: 1px solid rgba(125,234,255,.22);
          border-radius: 999px;
          background: rgba(0,212,255,.07);
          color: #A5F3FC;
          font: 700 var(--chi-body-compact) 'Space Grotesk', sans-serif;
        }

        .email-catalog__workspace {
          display: grid;
          grid-template-columns: minmax(24rem, .9fr) minmax(28rem, 1.1fr);
          gap: 1rem;
          align-items: start;
        }

        .email-catalog__table-wrap,
        .email-catalog__preview {
          overflow: hidden;
          border: 1px solid rgba(255,255,255,.09);
          border-radius: 1rem;
          background: linear-gradient(150deg, rgba(24,31,58,.9), rgba(9,13,29,.94));
        }

        .email-catalog__table-wrap { overflow-x: auto; }

        .email-catalog__table {
          width: 100%;
          min-width: 34rem;
          border-collapse: collapse;
        }

        .email-catalog__table th {
          padding: .75rem .85rem;
          background: rgba(255,255,255,.035);
          color: #728099;
          font-family: 'Space Grotesk', sans-serif;
          font-size: var(--chi-body-compact);
          font-weight: 750;
          letter-spacing: .08em;
          text-align: left;
          text-transform: uppercase;
        }

        .email-catalog__table th:last-child { width: 3rem; }

        .email-catalog__table td {
          padding: 0 .85rem;
          border-top: 1px solid rgba(255,255,255,.065);
          color: #CBD5E1;
          font: 600 var(--chi-body-compact) 'Inter', sans-serif;
          vertical-align: middle;
        }

        .email-catalog__table tr[data-selected] td {
          background: rgba(233,30,140,.09);
          color: #FBCFE8;
        }

        .email-catalog__select {
          display: block;
          width: 100%;
          min-height: 3.9rem;
          padding: .65rem 0;
          border: 0;
          background: transparent;
          color: inherit;
          text-align: left;
          cursor: pointer;
        }

        .email-catalog__select strong,
        .email-catalog__select span { display: block; }

        .email-catalog__select strong {
          color: #F8FAFC;
          font: 700 .86rem 'Space Grotesk', sans-serif;
        }

        .email-catalog__select span {
          margin-top: .2rem;
          color: #718096;
          font: 500 var(--chi-body-compact) 'Inter', sans-serif;
        }

        .email-catalog__icon-button {
          display: grid;
          place-items: center;
          width: 2.75rem;
          height: 2.75rem;
          flex: 0 0 2.75rem;
          border: 1px solid rgba(255,255,255,.1);
          border-radius: .55rem;
          background: rgba(255,255,255,.04);
          color: #94A3B8;
          cursor: pointer;
        }

        .email-catalog__icon-button[aria-pressed="true"] {
          border-color: rgba(233,30,140,.45);
          background: rgba(233,30,140,.16);
          color: #F9A8D4;
        }

        .email-catalog__select:focus-visible,
        .email-catalog__icon-button:focus-visible {
          outline: 2px solid #7DEAFF;
          outline-offset: 2px;
        }

        .email-catalog__preview { min-width: 0; }

        .email-catalog__preview-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: .75rem;
          padding: .9rem 1rem;
          border-bottom: 1px solid rgba(255,255,255,.08);
        }

        .email-catalog__preview-head p {
          margin: 0 0 .2rem;
          color: #F9A8D4;
          font: 750 var(--chi-body-compact) 'Space Grotesk', sans-serif;
          letter-spacing: .08em;
          text-transform: uppercase;
        }

        .email-catalog__preview-head h3 {
          margin: 0;
          color: #fff;
          font: 700 1rem 'Space Grotesk', sans-serif;
        }

        .email-catalog__preview-head > span {
          flex-shrink: 0;
          color: #64748B;
          font: 700 var(--chi-body-compact) 'Space Grotesk', sans-serif;
        }

        .email-catalog__preview-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: .65rem;
          flex-wrap: wrap;
        }

        .email-catalog__download {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: .45rem;
          min-height: 2.75rem;
          padding: .45rem .75rem;
          border: 1px solid rgba(125,234,255,.28);
          border-radius: .6rem;
          background: rgba(0,212,255,.08);
          color: #C8F7FF;
          font: 700 var(--chi-body-compact) 'Space Grotesk', sans-serif;
          cursor: pointer;
        }

        .email-catalog__download:disabled {
          opacity: .65;
          cursor: wait;
        }

        .email-catalog__download:focus-visible {
          outline: 2px solid #7DEAFF;
          outline-offset: 2px;
        }

        .email-catalog__ticket-sample-notice,
        .email-catalog__ticket-pdf-error {
          margin: 0;
          padding: .7rem 1rem;
          color: #D6C8A4;
          background: rgba(195,155,81,.08);
          border-bottom: 1px solid rgba(195,155,81,.13);
          font: 500 var(--chi-body-compact) 'Inter', sans-serif;
          line-height: 1.5;
        }

        .email-catalog__ticket-pdf-error {
          color: #FFD7D7;
          background: rgba(184,66,66,.12);
        }

        .email-catalog__ticket-confirmation {
          display: grid;
          gap: .3rem;
          padding: .8rem .9rem;
          border: 1px solid rgba(125,234,255,.18);
          border-radius: .7rem;
          background: rgba(125,234,255,.055);
          color: #E2E8F0;
          overflow-wrap: anywhere;
        }

        .email-catalog__ticket-confirmation span {
          color: #93A4BC;
          font: 700 var(--chi-body-compact) 'Space Grotesk', sans-serif;
          letter-spacing: .05em;
          text-transform: uppercase;
        }

        .email-catalog__ticket-confirmation strong {
          color: #F8FAFC;
          font: 700 .95rem 'Space Grotesk', sans-serif;
        }

        .email-catalog__ticket-confirmation small {
          color: #AAB8CA;
          font: 500 var(--chi-body-compact) 'Inter', sans-serif;
          line-height: 1.45;
        }

        .email-catalog__ticket-blocked,
        .email-catalog__ticket-send-error {
          margin: .7rem 0 0;
          color: #F3D9A2;
          font: 600 var(--chi-body-compact) 'Inter', sans-serif;
          line-height: 1.5;
        }

        .email-catalog__ticket-send-error {
          color: #FFD7D7;
        }

        .email-catalog__details {
          display: grid;
          gap: .55rem;
          margin: 0;
          padding: .85rem 1rem;
          border-bottom: 1px solid rgba(255,255,255,.07);
        }

        .email-catalog__details > div {
          display: grid;
          grid-template-columns: 4.5rem minmax(0, 1fr);
          gap: .65rem;
        }

        .email-catalog__details dt {
          color: #657289;
          font: 700 var(--chi-body-compact) 'Space Grotesk', sans-serif;
        }

        .email-catalog__details dd {
          margin: 0;
          color: #D8E0EC;
          font: 500 var(--chi-body-compact) 'Inter', sans-serif;
          line-height: 1.45;
        }

        .email-catalog__notice {
          margin: 0;
          padding: .65rem 1rem;
          background: rgba(0,212,255,.055);
          color: #9FB2C9;
          font: 500 var(--chi-body-compact) 'Inter', sans-serif;
          line-height: 1.45;
        }

        .email-catalog__frame-shell {
          padding: .75rem;
          background: #D9DEE8;
        }

        .email-catalog__frame-shell iframe {
          display: block;
          width: 100%;
          min-height: 42rem;
          border: 0;
          border-radius: .45rem;
          background: #fff;
        }

        .email-catalog__state,
        .email-catalog__preview-state {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: .55rem;
          min-height: 8rem;
          padding: 1rem;
          color: #94A3B8;
          font: 600 var(--chi-body-compact) 'Inter', sans-serif;
          text-align: center;
        }

        @media (max-width: 1080px) {
          .email-catalog__workspace { grid-template-columns: 1fr; }
          .email-catalog__frame-shell iframe { min-height: 36rem; }
        }

        @media (max-width: 600px) {
          .email-catalog__head { align-items: flex-start; flex-direction: column; }
          .email-catalog__count { align-self: flex-start; }
          .email-catalog__preview-head { flex-direction: column; }
          .email-catalog__preview-actions { width: 100%; justify-content: space-between; }
          .email-catalog__details > div { grid-template-columns: 1fr; gap: .15rem; }
          .email-catalog__frame-shell { padding: .35rem; }
          .email-catalog__frame-shell iframe { min-height: 32rem; }
        }
      `}</style>
    </section>

      <AlertDialog open={Boolean(testFor)} onOpenChange={open => { if (!open) setTestFor(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isTicketTest ? `Send ${testFor?.name} to me?` : `Send a test of ${testFor?.name}?`}
            </AlertDialogTitle>
            {isTicketTest ? (
              <>
                <AlertDialogDescription>
                  This sends a clearly marked sample to your signed-in owner email. It uses fictional attendee/order details only; it cannot resend an attendee email or alter a real order.
                </AlertDialogDescription>
                <div className="email-catalog__ticket-confirmation">
                  <span>Read-only recipient</span>
                  <strong>{ticketSettings?.recipient || "Not available"}</strong>
                  <small>Sample QR artwork is watermarked and cannot be used for entry. No real claim links are included.</small>
                </div>
                {ticketTestBlockedReason && (
                  <p className="email-catalog__ticket-blocked" role={ticketTestSettings.isLoading ? "status" : "alert"}>
                    {ticketTestBlockedReason}
                  </p>
                )}
                {ticketTestError && (
                  <p className="email-catalog__ticket-send-error" role="alert">{ticketTestError}</p>
                )}
              </>
            ) : (
              <AlertDialogDescription>
                <span style={{ display: "block" }}>
                  The email is rendered with the same fictional sample people the preview uses, and its
                  subject is prefixed with <strong style={{ color: "#F8FAFC" }}>[Test]</strong>.
                </span>
                <label style={{ display: "grid", gap: ".3rem", marginTop: ".8rem" }}>
                  <span style={{ color: "#CBD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>
                    Send to
                  </span>
                  <input
                    type="email"
                    autoFocus
                    value={testRecipient}
                    onChange={event => setTestRecipient(event.target.value)}
                    placeholder="you@example.com"
                    style={{ width: "100%", minHeight: 40, padding: ".5rem .7rem", borderRadius: ".5rem", border: "1px solid rgba(255,255,255,.16)", background: "rgba(255,255,255,.05)", color: "#FFFFFF", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", boxSizing: "border-box" }}
                  />
                </label>
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isTicketTest
                ? ticketTestBlocked || sendTicketEmailTest.isPending || ticketSendLock.current
                : sendTest.isPending || !testRecipient.trim()}
              onClick={event => {
                event.preventDefault();
                if (!testFor) return;
                if (isTicketEmailTestId(testFor.id)) {
                  if (ticketTestBlocked || ticketSendLock.current) return;
                  ticketSendLock.current = true;
                  setTicketTestError(null);
                  try {
                    const requestId = globalThis.crypto?.randomUUID?.();
                    if (!requestId) throw new Error("A secure request ID could not be created. No email was sent.");
                    sendTicketEmailTest.mutate({ id: testFor.id, requestId });
                  } catch (error) {
                    ticketSendLock.current = false;
                    setTicketTestError(error instanceof Error ? error.message : "The test could not be sent.");
                  }
                  return;
                }
                sendTest.mutate({ id: testFor.id, recipient: testRecipient.trim() });
              }}
            >
              {isTicketTest
                ? sendTicketEmailTest.isPending || ticketSendLock.current ? "Sending…" : "Send test to me"
                : sendTest.isPending ? "Sending…" : "Send test email"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}